import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Truck, Edit2, Save, X, Trash2, Plus, ScrollText, CalendarRange, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { TimelineRenderer } from "@/components/timeline/TimelineRenderer";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import type { Vehicle, LogEntry } from "@shared/types";
export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editName, setEditName] = useState("");
  const [editMake, setEditMake] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editYear, setEditYear] = useState("");
  const [editVin, setEditVin] = useState("");
  const [editOdometer, setEditOdometer] = useState("");
  const [editOdometerUnit, setEditOdometerUnit] = useState<"mi" | "km" | "hr">("mi");
  const [editStatus, setEditStatus] = useState<NonNullable<Vehicle["service_status"]>>("in_service");
  const [editNotes, setEditNotes] = useState("");
  const [logDesc, setLogDesc] = useState("");
  const [logDate, setLogDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [addingLog, setAddingLog] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const [v, logsData] = await Promise.all([
        authFetch<Vehicle>(`/api/vehicles/${id}`),
        authFetch<{ items: LogEntry[] }>("/api/logs"),
      ]);
      setVehicle(v);
      setLogs(
        (logsData.items ?? [])
          .filter((l) => l.entity_id === id)
          .sort((a, b) => b.date.localeCompare(a.date))
      );
      setEditName(v.name);
      setEditMake(v.make ?? "");
      setEditModel(v.model ?? "");
      setEditYear(String(v.year ?? ""));
      setEditVin(v.vin ?? "");
      setEditOdometer(String(v.odometer_value ?? ""));
      setEditOdometerUnit((v.odometer_unit ?? "mi") as "mi" | "km" | "hr");
      setEditStatus((v.service_status ?? "in_service") as NonNullable<Vehicle["service_status"]>);
      setEditNotes(v.notes ?? "");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load vehicle";
      setError(msg);
      console.error("[VehicleDetail]", err);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { loadData(); }, [loadData]);
  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await authFetch<Vehicle>(`/api/vehicles/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: editName.trim(),
          make: editMake.trim() || null,
          model: editModel.trim() || null,
          year: editYear ? parseInt(editYear, 10) : null,
          vin: editVin.trim() || null,
          odometer_value: editOdometer ? parseInt(editOdometer, 10) : null,
          odometer_unit: editOdometer ? editOdometerUnit : null,
          service_status: editStatus,
          notes: editNotes.trim() || null,
        }),
      });
      setVehicle(updated);
      setEditing(false);
      toast.success("Vehicle updated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to update");
      console.error("[VehicleSave]", err);
    } finally {
      setSaving(false);
    }
  };
  const handleArchive = async () => {
    if (!id) return;
    try {
      await authFetch(`/api/vehicles/${id}`, { method: "DELETE" });
      toast.success("Vehicle archived");
      navigate("/vehicles");
    } catch (err: any) {
      toast.error(err?.message || "Failed to archive");
    }
  };
  const handleAddLog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logDesc.trim() || !vehicle) return;
    setAddingLog(true);
    try {
      await authFetch("/api/logs", {
        method: "POST",
        body: JSON.stringify({
          log_type: "maintenance",
          entity_type: "vehicle",
          entity_id: id,
          entity_name: vehicle.name,
          description: logDesc.trim(),
          date: logDate,
          metadata: {},
        }),
      });
      toast.success("Log added");
      setLogDesc("");
      setShowLogForm(false);
      const logsData = await authFetch<{ items: LogEntry[] }>("/api/logs");
      setLogs(
        (logsData.items ?? [])
          .filter((l) => l.entity_id === id)
          .sort((a, b) => b.date.localeCompare(a.date))
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to add log");
    } finally {
      setAddingLog(false);
    }
  };
  if (loading) {
    return (
      <>
        <PageHeader title="" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Vehicles", href: "/vehicles" }, { label: "Loading..." }]} />
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </>
    );
  }
  if (error || !vehicle) {
    return (
      <>
        <PageHeader title="Vehicle Not Found" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Vehicles", href: "/vehicles" }, { label: "Error" }]} />
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">{error || "Vehicle not found"}</div>
      </>
    );
  }
  return (
    <>
      <PageHeader
        title={vehicle.name}
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Vehicles", href: "/vehicles" }, { label: vehicle.name }]}
      >
        {!editing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit2 className="h-4 w-4 mr-1" /> Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive border-destructive/30">
                  <Trash2 className="h-4 w-4 mr-1" /> Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive {vehicle.name}?</AlertDialogTitle>
                  <AlertDialogDescription>This will hide the vehicle from active lists.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground">Archive</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>
        )}
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-ranch-brown" /> Vehicle Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Name</Label><Input value={editName} onChange={(e) => setEditName(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Make</Label><Input value={editMake} onChange={(e) => setEditMake(e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Model</Label><Input value={editModel} onChange={(e) => setEditModel(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Year</Label><Input type="number" value={editYear} onChange={(e) => setEditYear(e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2"><Label>Odometer / Hours</Label><Input type="number" value={editOdometer} onChange={(e) => setEditOdometer(e.target.value)} /></div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select value={editOdometerUnit} onValueChange={(v) => setEditOdometerUnit(v as "mi" | "km" | "hr")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mi">mi</SelectItem>
                          <SelectItem value="km">km</SelectItem>
                          <SelectItem value="hr">hr</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Status</Label>
                    <Select value={editStatus} onValueChange={(v) => setEditStatus(v as NonNullable<Vehicle["service_status"]>)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_service">In service</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="out_of_service">Out of service</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>VIN</Label><Input value={editVin} onChange={(e) => setEditVin(e.target.value)} /></div>
                  <div className="space-y-2"><Label>Notes</Label><Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Make</p><p className="font-medium">{vehicle.make || "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Model</p><p className="font-medium">{vehicle.model || "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Year</p><p className="font-medium">{vehicle.year || "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Odometer</p><p className="font-medium">{vehicle.odometer_value !== null && vehicle.odometer_value !== undefined ? `${vehicle.odometer_value.toLocaleString()} ${vehicle.odometer_unit ?? "mi"}` : "—"}</p></div>
                  <div><p className="text-sm text-muted-foreground">Status</p><StatusBadge status={vehicle.service_status} /></div>
                  <div><p className="text-sm text-muted-foreground">VIN</p><p className="font-medium font-mono text-sm">{vehicle.vin || "—"}</p></div>
                  {vehicle.notes && <div className="col-span-2"><p className="text-sm text-muted-foreground">Notes</p><p className="text-sm">{vehicle.notes}</p></div>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ImageIcon className="h-4 w-4 text-ranch-brown" /> Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EntityImageGallery entityType="vehicle" entityId={vehicle.id} title="" />
            </CardContent>
          </Card>
        </div>
        <div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ScrollText className="h-4 w-4" /> Maintenance Log
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowLogForm(!showLogForm)}>
                <Plus className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {showLogForm && (
                <form onSubmit={handleAddLog} className="mb-4 p-3 rounded-lg bg-muted/50 space-y-3">
                  <Input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
                  <Textarea placeholder="What was done?" value={logDesc} onChange={(e) => setLogDesc(e.target.value)} rows={2} />
                  <Button type="submit" size="sm" disabled={addingLog} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                    {addingLog ? "Adding..." : "Add Entry"}
                  </Button>
                </form>
              )}
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No maintenance logs yet.</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="border-l-2 border-ranch-gold pl-3 py-1">
                      <p className="text-2xs text-muted-foreground">
                        {log.date ? format(parseISO(log.date + "T00:00:00"), "MMM d, yyyy") : ""}
                      </p>
                      <p className="text-sm text-foreground">{log.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-ranch-brown" />
          Timeline
        </h2>
        <TimelineRenderer entityType="vehicle" entityId={id!} />
      </div>
    </>
  );
}
